import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { fileUrl, fmtEUR, formatApiErrorDetail } from "@/lib/api";
import {
  ArrowLeft, ArrowUp, ArrowDown, Plus, Trash2, Save, X, Image as ImageIcon, Layers, Copy,
} from "lucide-react";
import { toast } from "sonner";

const emptyTier = { quantity: 1, price: 0 };
const emptyItem = () => ({
  name: "",
  print_type: "",
  color: "",
  product_code: "",
  supplier: "",
  description: "",
  photos: [],
  tiers: [{ ...emptyTier }],
});

const QuoteForm = () => {
  const { id } = useParams();
  const isNew = !id || id === "nova";
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ client_id: "", contact_id: "", items: [emptyItem()], extras: [{ label: "Transporte", amount: 0 }, { label: "Design / Arte-final", amount: 0 }], notes: "" });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/clients");
      setClients(data);
    })();
  }, []);

  useEffect(() => {
    if (!isNew) {
      (async () => {
        try {
          const { data } = await api.get(`/quotes/${id}`);
          setForm({
            client_id: data.client_id,
            contact_id: data.contact_id || "",
            items: (data.items || []).map((i) => ({
              ...emptyItem(),
              ...i,
              tiers: (i.tiers && i.tiers.length > 0) ? i.tiers : [{ ...emptyTier }],
            })),
            extras: (data.extras && data.extras.length > 0) ? data.extras : [],
            notes: data.notes || "",
          });
        } catch {
          toast.error("Cotação não encontrada");
          navigate("/cotacoes");
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [id, isNew, navigate]);

  const updateItem = (idx, k, v) => {
    setForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [k]: v };
      return { ...f, items };
    });
  };

  const updateTier = (itemIdx, tierIdx, k, v) => {
    setForm((f) => {
      const items = [...f.items];
      const tiers = [...(items[itemIdx].tiers || [])];
      tiers[tierIdx] = { ...tiers[tierIdx], [k]: v };
      items[itemIdx] = { ...items[itemIdx], tiers };
      return { ...f, items };
    });
  };

  const addTier = (itemIdx) => {
    setForm((f) => {
      const items = [...f.items];
      const tiers = [...(items[itemIdx].tiers || []), { ...emptyTier }];
      items[itemIdx] = { ...items[itemIdx], tiers };
      return { ...f, items };
    });
  };

  const removeTier = (itemIdx, tierIdx) => {
    setForm((f) => {
      const items = [...f.items];
      const tiers = (items[itemIdx].tiers || []).filter((_, i) => i !== tierIdx);
      items[itemIdx] = { ...items[itemIdx], tiers: tiers.length ? tiers : [{ ...emptyTier }] };
      return { ...f, items };
    });
  };

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const duplicateItem = (idx) =>
    setForm((f) => {
      const items = [...f.items];
      const copy = JSON.parse(JSON.stringify(f.items[idx]));
      copy.photos = [...(f.items[idx].photos || [])];
      copy.tiers = (f.items[idx].tiers || []).map((t) => ({ ...t }));
      items.splice(idx + 1, 0, copy);
      return { ...f, items };
    });
  const removeItem = (idx) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const moveItem = (idx, dir) =>
    setForm((f) => {
      const items = [...f.items];
      const target = idx + dir;
      if (target < 0 || target >= items.length) return f;
      [items[idx], items[target]] = [items[target], items[idx]];
      return { ...f, items };
    });

  const addExtra = () => setForm((f) => ({ ...f, extras: [...(f.extras || []), { label: "", amount: 0 }] }));
  const updateExtra = (idx, k, v) =>
    setForm((f) => ({
      ...f,
      extras: (f.extras || []).map((e, i) => (i === idx ? { ...e, [k]: v } : e)),
    }));
  const removeExtra = (idx) =>
    setForm((f) => ({ ...f, extras: (f.extras || []).filter((_, i) => i !== idx) }));

  const extrasTotal = (form.extras || []).reduce(
    (s, e) => s + (Number(e.amount) || 0), 0
  );

  // total range (sum of min tier across items, max tier across items) + extras
  const totalMin = form.items.reduce((s, it) => {
    const tiers = it.tiers || [];
    if (tiers.length === 0) return s;
    return s + Math.min(...tiers.map((t) => (Number(t.quantity) || 0) * (Number(t.price) || 0)));
  }, 0) + extrasTotal;
  const totalMax = form.items.reduce((s, it) => {
    const tiers = it.tiers || [];
    if (tiers.length === 0) return s;
    return s + Math.max(...tiers.map((t) => (Number(t.quantity) || 0) * (Number(t.price) || 0)));
  }, 0) + extrasTotal;

  const save = async (e) => {
    e.preventDefault();
    if (!form.client_id) {
      toast.error("Selecione um cliente");
      return;
    }
    if (form.items.length === 0) {
      toast.error("Adicione ao menos um item");
      return;
    }
    for (const it of form.items) {
      if (!it.tiers || it.tiers.length === 0) {
        toast.error("Cada item precisa de pelo menos uma quantidade");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        client_id: form.client_id,
        contact_id: form.contact_id || null,
        notes: form.notes,
        extras: (form.extras || [])
          .filter((e) => (e.label || "").trim() && Number(e.amount) > 0)
          .map((e) => ({ label: e.label.trim(), amount: Number(e.amount) })),
        items: form.items.map((i) => ({
          name: i.name,
          print_type: i.print_type,
          color: i.color,
          product_code: i.product_code,
          supplier: i.supplier,
          description: i.description,
          photos: i.photos || [],
          tiers: (i.tiers || []).map((t) => ({
            quantity: Number(t.quantity) || 0,
            price: Number(t.price) || 0,
          })),
        })),
      };
      if (isNew) {
        const { data } = await api.post("/quotes", payload);
        toast.success("Cotação criada");
        navigate(`/cotacoes/${data.id}`);
      } else {
        await api.put(`/quotes/${id}`, payload);
        toast.success("Cotação atualizada");
        navigate(`/cotacoes/${id}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-zinc-500">Carregando...</div>;

  return (
    <div className="max-w-5xl mx-auto fade-up" data-testid="quote-form-page">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-300 mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>

      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold mb-2">
          {isNew ? "Nova" : "Editar"}
        </div>
        <h1 className="font-display text-4xl font-black text-zinc-50 tracking-tighter">
          {isNew ? "Criar Cotação" : "Editar Cotação"}
        </h1>
      </div>

      <form onSubmit={save} className="space-y-6">
        {/* Cliente */}
        <section className="rounded-xl border border-zinc-800 bg-[#121214] p-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-3">
                Cliente (Empresa) *
              </div>
              <select
                value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value, contact_id: "" })}
                data-testid="quote-client-select"
                required
                className="w-full px-3 py-3 bg-[#09090b] border border-zinc-800 rounded-lg text-zinc-100 focus:border-zinc-500 focus:outline-none text-sm"
              >
                <option value="">— Selecione um cliente —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} {c.nif ? `· ${c.nif}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-3">
                Pessoa de Contato
              </div>
              {(() => {
                const selectedClient = clients.find((c) => c.id === form.client_id);
                const contacts = selectedClient?.contacts || [];
                return (
                  <>
                    <select
                      value={form.contact_id}
                      onChange={(e) => setForm({ ...form, contact_id: e.target.value })}
                      data-testid="quote-contact-select"
                      disabled={!form.client_id || contacts.length === 0}
                      className="w-full px-3 py-3 bg-[#09090b] border border-zinc-800 rounded-lg text-zinc-100 focus:border-zinc-500 focus:outline-none text-sm disabled:opacity-50"
                    >
                      <option value="">
                        {contacts.length === 0
                          ? form.client_id
                            ? "Sem contatos cadastrados"
                            : "Selecione um cliente primeiro"
                          : "— Geral / Sem pessoa específica —"}
                      </option>
                      {contacts.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}{c.role ? ` · ${c.role}` : ""}
                        </option>
                      ))}
                    </select>
                    {form.client_id && contacts.length === 0 && (
                      <button
                        type="button"
                        onClick={() => navigate(`/clientes/${form.client_id}`)}
                        className="mt-2 text-xs text-zinc-400 hover:text-zinc-200 underline"
                      >
                        Adicionar contato a este cliente
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
          {clients.length === 0 && (
            <button
              type="button"
              onClick={() => navigate("/clientes/novo")}
              className="mt-3 text-sm text-zinc-400 underline"
            >
              Cadastrar primeiro cliente
            </button>
          )}
        </section>

        {/* Itens */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1">
                Produtos
              </div>
              <h2 className="font-display text-xl font-bold text-zinc-100">Itens da cotação</h2>
            </div>
            <button
              type="button"
              onClick={addItem}
              data-testid="add-item-btn"
              className="flex items-center gap-2 px-4 py-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-200 rounded-lg text-sm font-medium transition-all"
            >
              <Plus className="w-4 h-4" /> Adicionar Item
            </button>
          </div>

          <div className="space-y-4">
            {form.items.map((it, idx) => (
              <ItemEditor
                key={idx}
                index={idx}
                item={it}
                onChange={(k, v) => updateItem(idx, k, v)}
                onTierChange={(tIdx, k, v) => updateTier(idx, tIdx, k, v)}
                onAddTier={() => addTier(idx)}
                onRemoveTier={(tIdx) => removeTier(idx, tIdx)}
                onRemove={() => removeItem(idx)}
                canRemove={form.items.length > 1}
                onDuplicate={() => duplicateItem(idx)}
                onMoveUp={() => moveItem(idx, -1)}
                onMoveDown={() => moveItem(idx, 1)}
                canMoveUp={idx > 0}
                canMoveDown={idx < form.items.length - 1}
              />
            ))}
          </div>
        </section>

        <section className="rounded-xl border border-zinc-800 bg-[#121214] p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1">Custos Adicionais</div>
              <div className="text-xs text-zinc-400">Transporte, design, manuseio, empacotamento... Somam ao total da proposta.</div>
            </div>
            <button
              type="button"
              onClick={addExtra}
              data-testid="add-quote-extra-btn"
              className="flex items-center gap-2 px-3 py-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-200 rounded-lg text-xs font-medium transition-all shrink-0"
            >
              <Plus className="w-3.5 h-3.5" /> Adicionar
            </button>
          </div>
          {(form.extras || []).length === 0 ? (
            <div className="text-xs text-zinc-600 italic">Nenhum custo extra adicionado.</div>
          ) : (
            <div className="space-y-2">
              {form.extras.map((e, i) => (
                <div key={i} data-testid={`quote-extra-row-${i}`} className="grid grid-cols-[1fr,160px,32px] gap-2 items-center">
                  <input
                    value={e.label}
                    onChange={(ev) => updateExtra(i, "label", ev.target.value)}
                    placeholder="Ex: Transporte, Design, Manuseio..."
                    data-testid={`quote-extra-label-${i}`}
                    className={inp}
                  />
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={e.amount}
                      onChange={(ev) => updateExtra(i, "amount", ev.target.value)}
                      placeholder="0,00"
                      data-testid={`quote-extra-amount-${i}`}
                      className={inp + " pr-8"}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500">€</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeExtra(i)}
                    data-testid={`remove-quote-extra-${i}`}
                    className="w-8 h-8 flex items-center justify-center text-zinc-500 hover:text-red-400 rounded transition-colors"
                    title="Remover"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <div className="pt-2 border-t border-zinc-800 mt-2 flex justify-between text-xs">
                <span className="text-zinc-500">Total custos extras</span>
                <span className="text-zinc-200 font-bold">{fmtEUR(extrasTotal)}</span>
              </div>
            </div>
          )}
        </section>

        <section className="rounded-xl border border-zinc-800 bg-[#121214] p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-3">
            Notas / Condições
          </div>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={3}
            data-testid="quote-notes-input"
            className="w-full px-3 py-2.5 bg-[#09090b] border border-zinc-800 rounded-lg text-zinc-100 placeholder-zinc-600 focus:border-zinc-500 focus:outline-none text-sm resize-none"
            placeholder="Prazo de entrega, condições de pagamento, validade da proposta..."
          />
        </section>

        <div className="flex items-center justify-between rounded-xl border border-zinc-800 bg-[#0e0e10] p-6 flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">
              Faixa de investimento
            </div>
            <div className="font-display text-2xl sm:text-3xl font-black text-zinc-50 tracking-tighter">
              {totalMin === totalMax ? (
                fmtEUR(totalMax)
              ) : (
                <>
                  <span className="text-zinc-400">{fmtEUR(totalMin)}</span>
                  <span className="text-zinc-600 mx-2 text-xl">—</span>
                  {fmtEUR(totalMax)}
                </>
              )}
            </div>
            <div className="text-xs text-zinc-500 mt-0.5">Variação por faixa de quantidade</div>
          </div>
          <button
            type="submit"
            disabled={saving}
            data-testid="quote-save-btn"
            className="flex items-center gap-2 px-6 py-3 bg-zinc-100 hover:bg-white text-zinc-900 font-bold rounded-lg text-sm transition-all disabled:opacity-60"
          >
            <Save className="w-4 h-4" strokeWidth={2} /> {saving ? "Salvando..." : "Salvar Cotação"}
          </button>
        </div>
      </form>
    </div>
  );
};

const inp =
  "w-full px-3 py-2.5 bg-[#09090b] border border-zinc-800 rounded-lg text-zinc-100 placeholder-zinc-600 focus:border-zinc-500 focus:outline-none text-sm";

const ItemEditor = ({
  index, item, onChange, onTierChange, onAddTier, onRemoveTier, onRemove, canRemove,
  onDuplicate, onMoveUp, onMoveDown, canMoveUp, canMoveDown,
}) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const newPaths = [];
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newPaths.push(data.path);
      }
      onChange("photos", [...(item.photos || []), ...newPaths]);
      toast.success(`${newPaths.length} foto(s) enviada(s)`);
    } catch {
      toast.error("Falha ao enviar fotos");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (p) =>
    onChange("photos", item.photos.filter((x) => x !== p));

  const accents = [
    { bg: "bg-[#1b1f27]", border: "border-l-sky-500", badge: "text-sky-300" },
    { bg: "bg-[#241d16]", border: "border-l-amber-500", badge: "text-amber-300" },
    { bg: "bg-[#16241c]", border: "border-l-emerald-500", badge: "text-emerald-300" },
    { bg: "bg-[#241620]", border: "border-l-fuchsia-500", badge: "text-fuchsia-300" },
    { bg: "bg-[#1f1626]", border: "border-l-violet-500", badge: "text-violet-300" },
  ];
  const a = accents[index % accents.length];

  return (
    <div
      className={`rounded-xl border border-zinc-800 border-l-4 ${a.border} ${a.bg} p-5 shadow-[0_4px_20px_rgba(0,0,0,0.4)]`}
      data-testid={`item-editor-${index}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`text-[10px] uppercase tracking-[0.2em] font-bold ${a.badge}`}>
          Item {String(index + 1).padStart(2, "0")}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={!canMoveUp}
            data-testid={`move-item-up-btn-${index}`}
            title="Mover para cima"
            className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={!canMoveDown}
            data-testid={`move-item-down-btn-${index}`}
            title="Mover para baixo"
            className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowDown className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onDuplicate}
            data-testid={`duplicate-item-btn-${index}`}
            title="Duplicar item"
            className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800 transition-colors"
          >
            <Copy className="w-4 h-4" />
          </button>
          {canRemove && (
            <button
              type="button"
              onClick={onRemove}
              data-testid={`remove-item-btn-${index}`}
              className="p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-zinc-800 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1.5">
            Nome do Item
          </label>
          <input
            value={item.name}
            onChange={(e) =>
              onChange(
                "name",
                e.target.value.replace(/(^|\s)(\p{L})/gu, (m, sep, ch) => sep + ch.toUpperCase())
              )
            }
            className={inp}
            placeholder="Ex: Caneta promocional"
            data-testid={`item-name-${index}`}
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1.5">
            Tipo de Impressão *
          </label>
          <input
            required
            value={item.print_type}
            onChange={(e) => onChange("print_type", e.target.value)}
            className={inp}
            placeholder="Serigrafia, Tampografia, DTF..."
            data-testid={`item-print-type-${index}`}
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1.5">
            Cor do Produto
          </label>
          <input
            value={item.color}
            onChange={(e) => onChange("color", e.target.value)}
            className={inp}
            placeholder="Ex: Preto, Azul-marinho, Pantone 286C..."
            data-testid={`item-color-${index}`}
          />
        </div>
      </div>

      {/* Internal-only fields */}
      <div className="mb-4 rounded-lg border border-zinc-800/60 bg-[#0a0a0c] p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">
            Controle Interno
          </div>
          <span className="text-[9px] uppercase tracking-wider text-zinc-600 border border-zinc-800 rounded-full px-2 py-0.5">
            Não aparece no PDF
          </span>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1.5">
              Código do Produto
            </label>
            <input
              value={item.product_code || ""}
              onChange={(e) => onChange("product_code", e.target.value)}
              className={inp}
              placeholder="Ex: BM-CN-001"
              data-testid={`item-product-code-${index}`}
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1.5">
              Fornecedor
            </label>
            <input
              value={item.supplier || ""}
              onChange={(e) => onChange("supplier", e.target.value)}
              className={inp}
              placeholder="Ex: Fornecedor Alfa Lda"
              data-testid={`item-supplier-${index}`}
            />
          </div>
        </div>
      </div>

      {/* Tiers */}
      <div className="mb-4 rounded-lg border border-zinc-800/80 bg-[#0e0e10] p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Layers className="w-3.5 h-3.5 text-zinc-500" strokeWidth={1.5} />
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-bold">
              Faixas de Quantidade & Preço
            </div>
          </div>
          <button
            type="button"
            onClick={onAddTier}
            data-testid={`add-tier-btn-${index}`}
            className="flex items-center gap-1.5 text-xs text-zinc-300 hover:text-zinc-100 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Faixa
          </button>
        </div>
        <div className="space-y-2">
          {(item.tiers || []).map((t, tIdx) => {
            const qty = Number(t.quantity) || 0;
            const price = Number(t.price) || 0;
            const sub = qty * price;
            return (
              <div
                key={tIdx}
                className="grid grid-cols-[1fr,1fr,auto,32px] gap-2 items-end"
                data-testid={`tier-row-${index}-${tIdx}`}
              >
                <div>
                  {tIdx === 0 && (
                    <label className="block text-[9px] uppercase tracking-[0.18em] text-zinc-600 font-bold mb-1">
                      Quantidade
                    </label>
                  )}
                  <input
                    type="number"
                    min="0"
                    step="1"
                    required
                    value={t.quantity}
                    onChange={(e) => onTierChange(tIdx, "quantity", e.target.value)}
                    className={inp + " py-2"}
                    data-testid={`tier-qty-${index}-${tIdx}`}
                  />
                </div>
                <div>
                  {tIdx === 0 && (
                    <label className="block text-[9px] uppercase tracking-[0.18em] text-zinc-600 font-bold mb-1">
                      Preço unitário (€)
                    </label>
                  )}
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    value={t.price}
                    onChange={(e) => onTierChange(tIdx, "price", e.target.value)}
                    className={inp + " py-2"}
                    data-testid={`tier-price-${index}-${tIdx}`}
                  />
                </div>
                <div className="text-right min-w-[100px]">
                  {tIdx === 0 && (
                    <label className="block text-[9px] uppercase tracking-[0.18em] text-zinc-600 font-bold mb-1">
                      Total
                    </label>
                  )}
                  <div className="text-zinc-100 font-bold text-sm py-2.5">{fmtEUR(sub)}</div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemoveTier(tIdx)}
                  className="w-8 h-9 flex items-center justify-center text-zinc-500 hover:text-red-400 rounded transition-colors mb-0.5"
                  aria-label="Remover faixa"
                  data-testid={`remove-tier-${index}-${tIdx}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
        <div className="text-[10px] text-zinc-600 mt-3">
          Adicione múltiplas faixas para escalonar o preço por volume (ex: 100un · 500un · 1000un).
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1.5">
          Descrição Detalhada
        </label>
        <textarea
          value={item.description}
          onChange={(e) => onChange("description", e.target.value)}
          rows={2}
          className={inp + " resize-none"}
          data-testid={`item-desc-${index}`}
          placeholder="Cores, dimensões, especificações técnicas..."
        />
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-2">
          Fotos do produto
        </label>
        <div className="flex flex-wrap gap-3">
          {(item.photos || []).map((p) => (
            <div
              key={p}
              className="relative w-24 h-24 rounded-lg border border-zinc-800 bg-[#0e0e10] overflow-hidden group"
            >
              <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removePhoto(p)}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/80 text-zinc-200 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            data-testid={`item-photo-upload-${index}`}
            disabled={uploading}
            className="w-24 h-24 rounded-lg border border-dashed border-zinc-700 bg-[#0e0e10] flex flex-col items-center justify-center text-zinc-500 hover:border-zinc-500 hover:text-zinc-300 transition-all disabled:opacity-50"
          >
            {uploading ? (
              <span className="text-xs">Enviando...</span>
            ) : (
              <>
                <ImageIcon className="w-5 h-5 mb-1" strokeWidth={1.5} />
                <span className="text-[10px] uppercase tracking-wider font-bold">Adicionar</span>
              </>
            )}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleUpload(e.target.files)}
          />
        </div>
      </div>
    </div>
  );
};

export default QuoteForm;
